import { Link } from 'react-router'
import { facetHref, splitFacetValue } from '@/site/lib/format'
import { MENU_FACETS } from '@/site/lib/search'
import { useSiteData } from '@/site/lib/siteData'
import type { Facet } from '@/site/lib/types'

/** Menu facets with their options, most tutorials first. */
function useMenu(): Facet[] {
  const { menu } = useSiteData()
  return MENU_FACETS.map((field) => menu.find((f) => f.field === field))
    .filter((f): f is Facet => !!f?.options?.length)
    .map((f) => ({ ...f, options: [...f.options].sort((a, b) => b.count - a.count) }))
}

function Chevron({ className }: { className?: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className={className} aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

/**
 * Filter menus from the menu facets. Desktop: a row of dropdowns (hover or keyboard focus). Phones: a panel
 * of expandable sections, shown by the header's menu button. Values with a collection page link there. No tutorial
 * counts: the menu is on every page, and the numbers were the same text repeated across the whole site.
 */
export default function FacetMenu({ mobileOpen }: { mobileOpen: boolean }) {
  const { collections } = useSiteData()
  const facets = useMenu()
  if (facets.length === 0) return null

  const option = (facet: Facet, value: string) => ({
    to: facetHref(facet.field, value, collections),
    label: splitFacetValue(facet.field, value).name,
  })

  return (
    <>
      <nav aria-label="Browse tutorials" className="hidden border-b border-qt-line md:block">
        <ul className="qt-container flex gap-9">
          {facets.map((facet) => (
            <li key={facet.field} className="group relative">
              <button type="button" aria-haspopup="true" className="flex items-center gap-1.5 pb-4 text-base font-bold">
                {facet.displayName} <Chevron />
              </button>
              <ul className="invisible absolute top-full left-0 z-50 max-h-[70vh] w-64 overflow-y-auto rounded-2xl border-2 border-qt-line bg-white p-2 opacity-0 shadow-lg transition group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
                {facet.options.map((opt) => {
                  const { to, label } = option(facet, opt.value)
                  return (
                    <li key={opt.value}>
                      <Link to={to} className="block rounded-xl px-3 py-2 text-sm font-semibold hover:bg-qt-soft">
                        {label}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ul>
      </nav>

      {/* Rendered only while open: otherwise every page would carry each menu link twice. */}
      {mobileOpen && (
        <nav id="site-menu" aria-label="Browse tutorials" className="border-b border-qt-line md:hidden">
          <ul className="qt-container divide-y divide-qt-line pb-2">
            <li>
              <Link to="/tutorials" className="block py-3 text-base font-bold">
                All tutorials
              </Link>
            </li>
            {facets.map((facet) => (
              <li key={facet.field}>
                <details className="group">
                  <summary className="flex list-none items-center justify-between py-3 text-base font-bold [&::-webkit-details-marker]:hidden">
                    {facet.displayName}
                    <Chevron className="transition group-open:rotate-180" />
                  </summary>
                  <ul className="grid grid-cols-2 gap-x-4 pb-3">
                    {facet.options.map((opt) => {
                      const { to, label } = option(facet, opt.value)
                      return (
                        <li key={opt.value}>
                          <Link to={to} className="block py-2 text-sm font-semibold">
                            {label}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </>
  )
}
